import React, { useState, useRef, useEffect } from 'react';
import ocrProcessor from '../utils/ocrProcessor';
import naturalLanguageParser from '../utils/naturalLanguageParser';

const VisualInput = ({ onAnswerKeyChange, onStudentWorkChange }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [ocrResults, setOcrResults] = useState(null);
  const [useCamera, setUseCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...imageFiles]);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setCameraStream(stream);
      setUseCamera(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setUseCamera(false);
  };

  const captureFromCamera = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setUploadedFiles(prev => [...prev, file]);
      }, 'image/jpeg');
    }
  };

  const processOCR = async () => {
    if (uploadedFiles.length === 0) {
      alert('Please upload images first');
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);
    setOcrResults(null);

    try {
      // Process all uploaded images
      const ocrResults = await ocrProcessor.processMultipleImages(
        uploadedFiles,
        { language: 'eng' },
        (progress) => {
          setProcessingProgress(progress.percent || 0);
        }
      );

      // Combine all OCR text results
      const allText = ocrResults
        .filter(result => result.success)
        .map(result => result.text)
        .join('\n\n');

      if (!allText.trim()) {
        throw new Error('No text could be extracted from the images. Please ensure images contain clear, readable text.');
      }

      // Try to parse as answer key first, then as student work
      let answerKeyData = null;
      let studentWorkData = null;

      try {
        // Attempt to parse as answer key
        const parsedAnswerKey = naturalLanguageParser.parseAnswerKey(allText);
        if (parsedAnswerKey.questions && parsedAnswerKey.questions.length > 0) {
          answerKeyData = parsedAnswerKey;
        }
      } catch (error) {
        console.log('Text does not appear to be an answer key:', error);
      }

      try {
        // Attempt to parse as student work
        const parsedStudentWork = naturalLanguageParser.parseStudentWork(allText);
        if (parsedStudentWork.answers && parsedStudentWork.answers.length > 0) {
          studentWorkData = parsedStudentWork;
        }
      } catch (error) {
        console.log('Text does not appear to be student work:', error);
      }

      // If we couldn't parse automatically, use OCR processor's parsing
      if (!answerKeyData && !studentWorkData) {
        // Try OCR processor's built-in parsing
        try {
          answerKeyData = ocrProcessor.parseOCRText(allText, 'answerKey');
          if (answerKeyData.questions && answerKeyData.questions.length > 0) {
            // Successfully parsed as answer key
          } else {
            // Try as student work
            studentWorkData = ocrProcessor.parseOCRText(allText, 'studentWork');
          }
        } catch (error) {
          console.error('Failed to parse OCR text:', error);
        }
      }

      // Prepare results
      const results = {
        rawText: allText,
        answerKey: answerKeyData || null,
        studentWork: studentWorkData || null,
        ocrResults: ocrResults
      };

      setOcrResults(results);

      // Update parent components if we have parsed data
      if (answerKeyData) {
        onAnswerKeyChange(JSON.stringify(answerKeyData, null, 2));
      }
      if (studentWorkData) {
        onStudentWorkChange(JSON.stringify(studentWorkData, null, 2));
      }

      setProcessingProgress(100);
    } catch (error) {
      console.error('OCR processing error:', error);
      alert(`OCR processing failed: ${error.message}. Please ensure images are clear and contain readable text.`);
      setProcessingProgress(0);
    } finally {
      setIsProcessing(false);
    }
  };

  // Cleanup OCR worker on unmount
  useEffect(() => {
    return () => {
      ocrProcessor.terminate().catch(console.error);
    };
  }, []);

  const clearAll = () => {
    setUploadedFiles([]);
    setOcrResults(null);
    setProcessingProgress(0);
    stopCamera();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
            Visual Input
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Upload images or use camera for handwritten/typed work
          </p>
        </div>
        <button
          onClick={clearAll}
          className="px-3 py-1.5 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          Clear All
        </button>
      </div>

      {/* Camera Section */}
      <div className={`p-4 rounded-lg border ${useCamera ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-gray-200 dark:border-gray-700'}`}>
        <div className="flex justify-between items-center mb-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Camera Capture
          </div>
          {!useCamera ? (
            <button
              onClick={startCamera}
              className="px-3 py-1.5 text-sm bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center"
            >
              <span className="mr-1">📷</span> Start Camera
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={captureFromCamera}
                className="px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Capture Photo
              </button>
              <button
                onClick={stopCamera}
                className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Stop Camera
              </button>
            </div>
          )}
        </div>

        {useCamera && (
          <div className="mt-3">
            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-48 object-cover"
              />
              <div className="absolute bottom-2 left-0 right-0 text-center">
                <div className="inline-block px-3 py-1 bg-black/50 text-white text-sm rounded-full">
                  Live Camera Feed
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Position document clearly in frame and click "Capture Photo"
            </div>
          </div>
        )}
      </div>

      {/* File Upload Section */}
      <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="font-medium text-gray-800 dark:text-gray-200 mb-3">
          File Upload
        </div>
        <div className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
          >
            <div className="text-4xl mb-2">📁</div>
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Click to upload images
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Supports JPG, PNG, PDF (images of handwritten/typed work)
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          {uploadedFiles.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Uploaded Files ({uploadedFiles.length})
              </div>
              <div className="space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-xl">🖼️</div>
                      <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200">
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Processing Section */}
      <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="font-medium text-gray-800 dark:text-gray-200 mb-3">
          OCR Processing
        </div>
        
        {isProcessing ? (
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300">Processing images...</span>
              <span className="text-primary">{processingProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Extracting text from images using OCR...
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={processOCR}
              disabled={uploadedFiles.length === 0 || isProcessing}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                uploadedFiles.length === 0 || isProcessing
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-dark text-white'
              }`}
            >
              {isProcessing ? 'Processing...' : 'Process Images with OCR'}
            </button>
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {uploadedFiles.length === 0
                ? 'Upload images first to enable OCR processing'
                : `Ready to process ${uploadedFiles.length} image(s)`}
            </div>
          </div>
        )}

        {ocrResults && !isProcessing && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center">
              <div className="text-green-500 mr-2">✅</div>
              <div>
                <div className="font-medium text-green-700 dark:text-green-300">
                  OCR Processing Complete
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">
                  {ocrResults.answerKey && `Extracted ${ocrResults.answerKey.questions?.length || 0} questions. `}
                  {ocrResults.studentWork && `Extracted ${ocrResults.studentWork.answers?.length || 0} answers. `}
                  {!ocrResults.answerKey && !ocrResults.studentWork && 'Text extracted. Please review and format manually.'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* OCR Results Preview */}
      {ocrResults && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {ocrResults.answerKey && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">
                  Extracted Answer Key
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  {ocrResults.answerKey.questions?.length || 0} questions detected
                </div>
              </div>
            )}
            {ocrResults.studentWork && (
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="font-medium text-purple-700 dark:text-purple-300 mb-1">
                  Extracted Student Work
                </div>
                <div className="text-sm text-purple-600 dark:text-purple-400">
                  Student: {ocrResults.studentWork.studentName || 'Unknown'}
                </div>
              </div>
            )}
          </div>
          {ocrResults.rawText && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                Extracted Text Preview
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 max-h-32 overflow-y-auto font-mono whitespace-pre-wrap">
                {ocrResults.rawText.substring(0, 500)}
                {ocrResults.rawText.length > 500 && '...'}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
        <div className="flex items-start">
          <div className="text-yellow-500 mr-2 mt-0.5">⚠️</div>
          <div>
            <div className="font-medium text-yellow-700 dark:text-yellow-300">Tips for Best OCR Results</div>
            <ul className="text-sm text-yellow-600 dark:text-yellow-400 mt-1 space-y-1">
              <li>• Use clear, high-contrast images with good lighting</li>
              <li>• Ensure text is straight and in focus</li>
              <li>• Printed text works better than handwriting</li>
              <li>• Always review extracted text for accuracy before grading</li>
              <li>• OCR accuracy depends on image quality and text clarity</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualInput;
